import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { TextField, InputAdornment, IconButton, Tooltip, Grid } from '@mui/material';
import Iconify from '../../iconify';
import { useScopeCheck } from '../../scopes';
import { useSelector } from '../../../redux/store';
import TicketSearchQuickDropdown from '../../../components/dashboard/advanced-search/TicketSearchQuickDropdown';
import OrderSearchQuickDropdown from '../../../components/dashboard/advanced-search/OrderSearchQuickDropdown';
import InvoiceSearchQuickDropdown from '../../../components/dashboard/advanced-search/InvoiceSearchQuickDropdown';
import { GetGridKeys } from '../dataGridHelper';

CustomQuickFilter.propTypes = {
  gridId: PropTypes.string,
  setQuickFilter: PropTypes.func,
  setActiveFilter: PropTypes.func,
  setFilters: PropTypes.func,
  sx: PropTypes.object,
  applyQuickFilter: PropTypes.bool,
  isModal: PropTypes.bool,
  advancedFilter: PropTypes.bool,
  advancedSearchBase: PropTypes.string,
  presetFilter: PropTypes.string,
  columns: PropTypes.array,
};

export default function CustomQuickFilter({
  gridId,
  setFilters,
  setQuickFilter,
  setActiveFilter,
  sx,
  applyQuickFilter,
  isModal,
  advancedFilter,
  advancedSearchBase,
  presetFilter,
  columns = []
}) {
  const { quickFilterKey, filterStorageKey: filtersStorageKey, searchKey, baseSearchTextStorageKey } = GetGridKeys(gridId);
  const { user } = useSelector((state) => state.user);

  const savedSearch = JSON.parse(localStorage.getItem(searchKey));
  const isFilter = localStorage.getItem(filtersStorageKey);
  const isRecent = savedSearch && new Date().getTime() - savedSearch?.timestamp < 300000;

  const hasFacilityAccess = useScopeCheck(['Facility-Manager']);

  const [searchValue, setSearchValue] = useState(isRecent && !isModal ? savedSearch?.search : presetFilter || '');

  useEffect(() => {
    const savedQuickFilter = JSON.parse(localStorage.getItem(quickFilterKey));
    if (savedQuickFilter && savedQuickFilter.manager) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        manager: savedQuickFilter.manager,
      }));
      setFilterIsActive(true);
    }
  }, []);

  useEffect(() => {
    if (isRecent && !isModal) {
      if (savedSearch?.search !== searchValue) {
        setSearchValue(savedSearch?.search || searchValue || '');
      }
    } else {
      setSearchValue(searchValue || '');
    }
  }, [searchValue, savedSearch, isModal]);

  const [filterIsActive, setFilterIsActive] = useState(false);
  const [quickAdvancedFilter, setQuickAdvancedFilter] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState(searchValue);
  const searchInputRef = useRef(null);

  const applyQuickFilterFunc = () => {
    const manager = { value: user.firstName, operator: 'contains' };
    setFilters((prevFilters) => ({ ...prevFilters, manager }));
    setFilterIsActive(true);
    localStorage.setItem(quickFilterKey, JSON.stringify({ manager, timestamp: new Date().getTime() }));
  };

  useEffect(() => {
    const handler = setTimeout(() => { setDebouncedValue(searchValue); }, 500);
    return () => { clearTimeout(handler); };
  }, [searchValue]);

  useEffect(() => {
    setQuickFilter(debouncedValue);
  }, [debouncedValue]);

  const handleSearch = (event) => {
    setSearchValue(event?.target.value);
    const searchValueWithTimestamp = {
      search: event?.target.value,
      timestamp: new Date().getTime(),
    };
    localStorage.setItem(searchKey, JSON.stringify(searchValueWithTimestamp));
  };

  const handleClear = () => {
    setSearchValue('');
    setQuickFilter('');
    setActiveFilter(null);
    setFilterIsActive(false);
    setFilters({});
    localStorage.removeItem(searchKey);
    localStorage.removeItem(filtersStorageKey);
    localStorage.removeItem(quickFilterKey);
    columns.forEach((c) => {
      localStorage.removeItem(`${baseSearchTextStorageKey}-${c}`);
    });
  };

  const handleAdvancedSearch = () => setQuickAdvancedFilter(true);

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="flex-end"
        alignItems="flex-end"
        sx={{
          mt: 1,
          display: 'flex', // Ensure it's a flex container
          flexWrap: 'nowrap', // Prevent wrapping of items
        }}
      >
        {hasFacilityAccess && applyQuickFilter && (
          <Grid item>
            <Tooltip title="Quick filter my name" arrow>
              <IconButton color="info" onClick={applyQuickFilterFunc}>
                <Iconify icon="tabler:filter-bolt" />
              </IconButton>
            </Tooltip>
          </Grid>
        )}

        {(searchValue || isFilter || filterIsActive) && (
          <Grid item>
            <Tooltip title="Clear all filters" arrow>
              <IconButton color="warning" onClick={handleClear}>
                <Iconify icon="tabler:filter-x" />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        <Grid item sx={{ flexGrow: 1, minWidth: 0, flexShrink: 1, display: 'flex' }}>
          <TextField
            ref={searchInputRef}
            value={searchValue}
            onChange={handleSearch}
            size="small"
            placeholder="Search..."
            autoComplete="off"
            sx={{
              ...sx,
              width: '100%', // Use available space
              // minWidth: '100px', // Minimum width to prevent elements becoming too small
            }}
            type="text"
            onKeyDown={(e) => e.key === "Enter" && advancedFilter && searchValue ? handleAdvancedSearch() : null}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {advancedFilter && advancedSearchBase === 'tickets' ?
                    <TicketSearchQuickDropdown
                      searchValue={searchValue}
                      quickAdvancedFilter={quickAdvancedFilter}
                      setQuickAdvancedFilter={setQuickAdvancedFilter}
                      searchInputRef={searchInputRef}
                    /> : advancedFilter && advancedSearchBase?.toLowerCase().includes('orders') ?
                      <OrderSearchQuickDropdown
                        searchValue={searchValue}
                        quickAdvancedFilter={quickAdvancedFilter}
                        setQuickAdvancedFilter={setQuickAdvancedFilter}
                        advancedSearchBase={advancedSearchBase}
                        searchInputRef={searchInputRef}
                      /> : advancedFilter && advancedSearchBase?.toLowerCase().includes('invoicing') ?
                        <InvoiceSearchQuickDropdown
                          searchValue={searchValue}
                          quickAdvancedFilter={quickAdvancedFilter}
                          setQuickAdvancedFilter={setQuickAdvancedFilter}
                          advancedSearchBase={advancedSearchBase}
                          searchInputRef={searchInputRef}
                        /> : null}
                </InputAdornment>
              ),
              style: { height: '30px', borderRadius: '4px', border: `solid 1px #404042` },
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}